import React, { useState } from "react"
import { useAtom } from "jotai"
import { SquareChevronRight, RectangleEllipsis } from "lucide-react"
import { MiniAppText } from '@/ui/molecules/mini-app-text'
import { NewTabData, useTabData } from '@sourcepilot/ui/framework/state/session-data';
import { productMiniAppStateAtom } from '@/state/rflp-session';import { AssemblyType } from '@/types/enums';
import { FunctionalSubsystem, FunctionalModule, Part } from "@/types/workspace"
import { iconMap } from '@sourcepilot/ui/framework/ui/atoms/icons';

type FunctionalUnit = FunctionalSubsystem | FunctionalModule | Part;

export const ModuleTree = (
  { 
    funcUnit, slug, containerWidth, depth = 0,
  } : {
    funcUnit: FunctionalUnit | null,
    slug: string,
    depth?: number,
    containerWidth:number,
  }
) => {
    const { activeTab, navigateToTab } = useTabData()
    const [hoveredItem, setHoveredItem] = useState('')
    const [miniAppState, setMiniAppState] = useAtom(productMiniAppStateAtom)
    const expandedItems = miniAppState.expandedItems

    // Reduce the indentation amount per level
    const cWidth = containerWidth - 24 - (depth * 12);

    const toggleExpanded = (id: string) => {
      if (expandedItems.includes(id)) {
        setMiniAppState({
          ...miniAppState,
          expandedItems: expandedItems.filter(item => item !== id),
        })
      } else {
        setMiniAppState({
          ...miniAppState,
          expandedItems: [...expandedItems, id],
        })
      }
    }

    // Get the module icon from iconMap
    const ModuleIcon = iconMap['module'];
    const PartIcon = iconMap['part'];

    // Icon styles
    const treeIcon = (node:FunctionalSubsystem | FunctionalModule | Part) => {
      const isPart = "quantity" in node;
      // const hasChildren = !isPart && (node.modules && node.modules.length > 0) || ("parts" in node && node.parts && node.parts.length > 0);
      const hasChildren = !isPart;
      const commonStyle = "w-3 h-3 mx-1 rounded";
      const hoverStyle = 'hover:bg-[#00ccaa] hover:shadow-xl';


      if (!hasChildren) {
        return <span className="w-1"></span>;
      }

      const isExpanded = hasChildren && expandedItems.includes(node.id);
      return (
        <button
          onClick={() => hasChildren && toggleExpanded(node.id)}
          className="hover:!text-white"
        >
          <SquareChevronRight className={`${commonStyle} ${hoverStyle} transition-transform text-[#4d30a0] ${isExpanded ? 'transform rotate-90' : ''}`} />
        </button>
      );
    }

    const subTree = (node:FunctionalUnit): React.ReactElement => {
      const isPart = "quantity" in node;

      const nodeTab: NewTabData = {
        id: node.id,
        title: node.name,
        contentType: isPart ? AssemblyType.PART : AssemblyType.MODULE,
        productSlug: slug,
      }

      const isExpanded = expandedItems.includes(node.id);
      const isActive = nodeTab.id === activeTab?.id;
      const isHovered = nodeTab.id === hoveredItem;

      return (
        <div key={node.id} className='ml-[10px] border-l border-gray-300 border-dotted text-[14px]'>
          <div className={`flex items-center`}>
            {treeIcon(node)}

            <button
              className={`text-left flex-grow flex items-center`}
              onClick={() => navigateToTab(nodeTab)}
              onMouseEnter={() => setHoveredItem(nodeTab.id)}
              onMouseLeave={() => setHoveredItem('')}
            >
              {isPart ? <PartIcon className="w-3 h-3 mr-1" /> : <ModuleIcon className="w-3 h-3 mr-1" />}
              <MiniAppText
                className=""
                containerWidth={cWidth - 8} // Adjust for icon width
                isActive={isActive}
                isHovered={isHovered}
              >
                {node.name}
              </MiniAppText>
            </button>
          </div>
          {!isPart && isExpanded && (
            <>
              {"parts" in node &&
                node.parts?.map((child: Part) => (
                  <div key={child.id}>{subTree(child)}</div>
                ))}
    
              {"modules" in node &&
                node.modules?.map((child: FunctionalModule) => (
                  <div key={child.id}>{subTree(child)}</div>
                ))}
            </>
          )}
        </div>
      )
    }

    if (!funcUnit) {
      return null;
    }

    return subTree(funcUnit);
}